import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  DollarSign,
  TrendingUp,
  Gift,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Calculator,
  Target,
  Clock,
  Star,
  CheckCircle2,
  Zap,
  Sparkles
} from "lucide-react";

const sectionLabels = {
  listings: "Listings",
  reviews: "Reviews",
  social: "Social Media",
  website: "Website",
  seo: "SEO",
  advertising: "Advertising",
  ecommerce: "Ecommerce",
  tech_stack: "Tech Stack"
};

const priorityColors = {
  high: "bg-red-100 text-red-700 border-red-200",
  medium: "bg-yellow-100 text-yellow-700 border-yellow-200",
  low: "bg-green-100 text-green-700 border-green-200"
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
};

export default function BudgetMaker({ budget, businessName }) {
  const [expandedSections, setExpandedSections] = useState({});
  const [activeTab, setActiveTab] = useState("overview");

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (!budget) return null;

  return (
    <div className="space-y-6">
      {/* Budget Overview Header */}
      <Card className="border-slate-200 overflow-hidden" data-testid="budget-overview">
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Calculator className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Proposed Budget</h2>
              <p className="text-white/80">Investment plan for {businessName}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-white/70 text-sm mb-1">Monthly Investment</div>
              <div className="text-2xl font-bold">{formatCurrency(budget.total_monthly_cost)}</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-white/70 text-sm mb-1">Setup Cost</div>
              <div className="text-2xl font-bold">{formatCurrency(budget.total_setup_cost)}</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-white/70 text-sm mb-1">Expected Monthly Value</div>
              <div className="text-2xl font-bold text-emerald-300">{formatCurrency(budget.expected_monthly_value)}</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-white/70 text-sm mb-1">ROI Multiplier</div>
              <div className="text-2xl font-bold text-yellow-300">{budget.roi_multiplier}x</div>
            </div>
          </div>
        </div>
        
        <CardContent className="p-6">
          {/* ROI Visualization */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              Return on Investment
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-sm text-slate-600 mb-2">Your Investment</div>
                <div className="text-3xl font-bold text-slate-900">{formatCurrency(budget.total_annual_cost)}</div>
                <div className="text-xs text-slate-500">First Year Total</div>
              </div>
              <div className="flex items-center justify-center">
                <div className="text-4xl">→</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-slate-600 mb-2">Expected Value</div>
                <div className="text-3xl font-bold text-emerald-600">{formatCurrency(budget.expected_annual_value)}</div>
                <div className="text-xs text-slate-500">First Year Return</div>
              </div>
            </div>
            <div className="mt-4 text-center">
              <Badge className="bg-emerald-100 text-emerald-700 text-lg px-4 py-1">
                {budget.roi_multiplier}x Return on Investment
              </Badge>
            </div>
          </div>
          
          {/* Timeline */}
          <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
            <Clock className="h-5 w-5 text-blue-600" />
            <div>
              <span className="font-medium text-slate-900">Implementation Timeline:</span>
              <span className="text-slate-600 ml-2">{budget.implementation_timeline}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Detailed View */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 h-12">
          <TabsTrigger value="overview" className="text-sm">
            <DollarSign className="h-4 w-4 mr-2" />
            Service Breakdown
          </TabsTrigger>
          <TabsTrigger value="free" className="text-sm">
            <Gift className="h-4 w-4 mr-2" />
            Free Resources
          </TabsTrigger>
          <TabsTrigger value="tracker" className="text-sm">
            <Target className="h-4 w-4 mr-2" />
            Value Tracker
          </TabsTrigger>
        </TabsList>

        {/* Service Breakdown Tab */}
        <TabsContent value="overview" className="space-y-4 mt-6">
          {budget.breakdowns.map((breakdown) => (
            <Card key={breakdown.section} className="border-slate-200" data-testid={`breakdown-${breakdown.section}`}>
              <CardHeader 
                className="cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleSection(breakdown.section)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-lg">{sectionLabels[breakdown.section]}</CardTitle>
                    <Badge variant="outline">
                      {breakdown.current_score}% → {breakdown.target_score}%
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    {breakdown.paid_services.length > 0 ? (
                      <div className="text-right">
                        <div className="text-lg font-bold text-slate-900">{formatCurrency(breakdown.section_monthly_cost)}/mo</div>
                        <div className="text-xs text-emerald-600">+{formatCurrency(breakdown.expected_monthly_value)} value</div>
                      </div>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700">Free Tools Available</Badge>
                    )}
                    {expandedSections[breakdown.section] ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </div>
                </div>
              </CardHeader>
              
              {expandedSections[breakdown.section] && (
                <CardContent className="pt-0 space-y-4">
                  {/* Paid Services */}
                  {breakdown.paid_services.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                        <Zap className="h-4 w-4 text-amber-500" />
                        Recommended Services
                      </h4>
                      <div className="space-y-3">
                        {breakdown.paid_services.map((service, idx) => (
                          <div key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h5 className="font-semibold text-slate-900">{service.name}</h5>
                                <p className="text-sm text-slate-600">{service.description}</p>
                              </div>
                              <Badge className={`${priorityColors[service.priority]} border`}>
                                {service.priority} priority
                              </Badge>
                            </div>
                            <div className="grid grid-cols-4 gap-4 mt-3 text-sm">
                              <div>
                                <span className="text-slate-500">Monthly</span>
                                <div className="font-semibold">{formatCurrency(service.monthly_cost)}</div>
                              </div>
                              <div>
                                <span className="text-slate-500">Setup</span>
                                <div className="font-semibold">{formatCurrency(service.setup_cost)}</div>
                              </div>
                              <div>
                                <span className="text-slate-500">Expected Value</span>
                                <div className="font-semibold text-emerald-600">{formatCurrency(service.expected_value)}/mo</div>
                              </div>
                              <div>
                                <span className="text-slate-500">Timeline</span>
                                <div className="font-semibold">{service.timeline}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Free Resources for this section */}
                  <div>
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <Gift className="h-4 w-4 text-emerald-500" />
                      Free Resources ({breakdown.free_resources.length})
                    </h4>
                    <div className="grid md:grid-cols-2 gap-2">
                      {breakdown.free_resources.map((resource, idx) => (
                        <a
                          key={idx}
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-3 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors flex items-start gap-3 group"
                        >
                          <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="font-medium text-slate-900 group-hover:text-emerald-700 flex items-center gap-1">
                              {resource.name}
                              <ExternalLink className="h-3 w-3 opacity-50" />
                            </div>
                            <p className="text-xs text-slate-600">{resource.description}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </TabsContent>

        {/* Free Resources Tab */}
        <TabsContent value="free" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50">
              <CardTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-emerald-600" />
                Free Tools & Resources
              </CardTitle>
              <CardDescription>
                {budget.free_resources_summary.length} free resources to improve your online presence immediately
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Group by category */}
                {Object.entries(
                  budget.free_resources_summary.reduce((acc, resource) => {
                    if (!acc[resource.category]) acc[resource.category] = [];
                    acc[resource.category].push(resource);
                    return acc;
                  }, {})
                ).map(([category, resources]) => (
                  <div key={category} className="space-y-2">
                    <h4 className="font-semibold text-slate-900 text-sm uppercase tracking-wider">{category}</h4>
                    {resources.map((resource, idx) => (
                      <a
                        key={idx}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 bg-white border border-slate-200 rounded-lg hover:border-emerald-300 hover:shadow-sm transition-all group"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-900 group-hover:text-emerald-700">{resource.name}</span>
                          <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-emerald-600" />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{resource.description}</p>
                      </a>
                    ))}
                  </div>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-slate-900">Pro Tip</h4>
                    <p className="text-sm text-slate-600">
                      Start with the free resources marked as "ESSENTIAL" first. These alone can improve your online presence by 20-30% with zero cost.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Value Tracker Tab */}
        <TabsContent value="tracker" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-600" />
                Investment vs Value Tracker
              </CardTitle>
              <CardDescription>
                Track your investment against expected returns (2-3x multiplier)
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Overall Tracker */}
              <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                <h4 className="font-semibold text-slate-900 mb-4">Annual Investment Analysis</h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Your Investment</span>
                      <span className="font-semibold">{formatCurrency(budget.total_annual_cost)}</span>
                    </div>
                    <Progress value={100} className="h-3 bg-slate-200" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Break-Even Point (1x)</span>
                      <span className="font-semibold">{formatCurrency(budget.total_annual_cost)}</span>
                    </div>
                    <Progress value={100} className="h-3 bg-yellow-200" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Expected Value (2x)</span>
                      <span className="font-semibold text-emerald-600">{formatCurrency(budget.total_annual_cost * 2)}</span>
                    </div>
                    <Progress value={200 / 3 * 100} className="h-3 bg-emerald-300" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Projected Value ({budget.roi_multiplier}x)</span>
                      <span className="font-semibold text-emerald-700">{formatCurrency(budget.expected_annual_value)}</span>
                    </div>
                    <Progress value={budget.roi_multiplier / 3 * 100} className="h-3 bg-emerald-500" />
                  </div>
                </div>
              </div>

              {/* Section by Section */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-4">Section-by-Section Value</h4>
                <div className="space-y-3">
                  {budget.breakdowns.filter(b => b.section_monthly_cost > 0).map((breakdown) => {
                    const monthlyROI = breakdown.expected_monthly_value / breakdown.section_monthly_cost;
                    return (
                      <div key={breakdown.section} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-slate-900">{sectionLabels[breakdown.section]}</span>
                          <Badge className="bg-emerald-100 text-emerald-700">{monthlyROI.toFixed(1)}x ROI</Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-slate-500">Cost</span>
                            <div className="font-semibold">{formatCurrency(breakdown.section_monthly_cost)}/mo</div>
                          </div>
                          <div>
                            <span className="text-slate-500">Value</span>
                            <div className="font-semibold text-emerald-600">{formatCurrency(breakdown.expected_monthly_value)}/mo</div>
                          </div>
                          <div>
                            <span className="text-slate-500">Net Gain</span>
                            <div className="font-semibold text-emerald-700">+{formatCurrency(breakdown.expected_monthly_value - breakdown.section_monthly_cost)}/mo</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Cost Comparison Note */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-slate-900 mb-2">📊 Industry Cost Comparison</h4>
                <p className="text-sm text-slate-600">
                  These services typically cost 2-3x more when purchased individually or through traditional agencies. 
                  Our recommended package provides maximum value at optimized pricing.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
